import type { UseQueryResult } from '@tanstack/react-query';
import { FlatListWithTabs } from 'app/atoms/flatlist-with-tabs';
import { EmptyGraphic } from 'app/components/empty-graphic';
import { withErrorBoundary } from 'app/components/error-boundary';
import { isTimelineLoanCardNewMonth, TimelineLoanCard } from 'app/components/timeline-loan-card';
import { useTPrefix } from 'app/components/management/title';
import { Tabs } from 'app/components/tabs';
import { ListPage } from 'app/design/primitives/page/list';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { LoanFilterKey, useAllLoans } from 'app/network/loan';
import type { Loan } from 'app/types/loan';
import { isLoanPendingCurrentUserRating, LoanType } from 'app/types/loan';
import { TransactionMode } from 'app/types/listing';
import { getListingTransactionMode } from 'app/lib/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, View } from 'react-native';
import { withOptionalMetrics } from '../../utils/performance/with-metrics';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';

import { formatCurrency } from 'app/localization/i18n';
import { useUserCurrency } from 'app/network/user';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'app/atoms';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconLeaf } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

type TypeFilter = 'all' | TransactionMode;

function getLoanMode(loan: Loan): TransactionMode {
  return getListingTransactionMode(loan.listing);
}

const PastLoansScreenComponent: NavigationScreen<'past-loans'> = ({ route }) => {
  const { t } = useTranslation('loans');

  const setPageTitle = usePageTitle('loans');
  useEffect(() => setPageTitle('past.title'), [setPageTitle]);

  const tPrefix = useTPrefix();
  const isHauteGaspesie = isWhiteLabel(WhiteLabel.HauteGaspesie);
  const routeTypeFilter = route.params?.transactionMode ?? 'all';
  const [typeFilterState, setTypeFilterState] = useState<{ source: TypeFilter; value: TypeFilter }>(() => ({
    source: routeTypeFilter,
    value: routeTypeFilter,
  }));
  let currentTypeFilterState = typeFilterState;
  if (typeFilterState.source !== routeTypeFilter) {
    currentTypeFilterState = { source: routeTypeFilter, value: routeTypeFilter };
    setTypeFilterState(currentTypeFilterState);
  }
  const typeFilter = currentTypeFilterState.value;
  const setTypeFilter = (value: TypeFilter) => setTypeFilterState({ source: routeTypeFilter, value });

  const myLendingsQuery = useAllLoans(LoanFilterKey.PastLoans, LoanType.Lending, {
    throwOnError: true,
  });

  const myBorrowingsQuery = useAllLoans(LoanFilterKey.PastLoans, LoanType.Borrowing, {
    throwOnError: true,
  });

  const ratingFilter = route.params?.ratingFilter;

  const LendingsList = (() => {
    return (
      <RenderedList
        query={myLendingsQuery}
        type={LoanType.Lending}
        ratingFilter={ratingFilter}
        typeFilter={typeFilter}
        onFilterChange={setTypeFilter}
        isHauteGaspesie={isHauteGaspesie}
      />
    );
  })();

  const BorrowingsList = (() => {
    return (
      <RenderedList
        query={myBorrowingsQuery}
        type={LoanType.Borrowing}
        ratingFilter={ratingFilter}
        typeFilter={typeFilter}
        onFilterChange={setTypeFilter}
        isHauteGaspesie={isHauteGaspesie}
      />
    );
  })();

  const tabs = {
    [LoanType.Lending]: LendingsList,
    [LoanType.Borrowing]: BorrowingsList,
  };

  useRegisterLoading(myLendingsQuery.isPending, 'PastLoansScreen');
  useRegisterLoading(myBorrowingsQuery.isPending, 'PastLoansScreen');

  return (
    <>
      <Text
        fontWeight="600"
        marginBottom="spacing.general.x4"
        marginTop="spacing.general.x5"
        textAlign="center"
        variant="headerXSmall"
      >
        {t('past.title')}
      </Text>

      <FlatListWithTabs
        i18nNS={`loans.${tPrefix}past`}
        initialTab={route.params?.type ?? LoanType.Lending}
        onTabChanged={() => setTypeFilter('all')}
        screenName="past-loans"
        tabs={tabs}
      />
    </>
  );
};

export const PastLoansScreen: NavigationScreen<'past-loans'> = withOptionalMetrics(
  'PastLoansScreen',
  withErrorBoundary(withDashboardLayout(PastLoansScreenComponent, 'simpleVerticalScrolling', true)),
);

const RenderedList = ({
  query,
  type,
  ratingFilter,
  typeFilter,
  onFilterChange,
  isHauteGaspesie,
}: {
  query: UseQueryResult<Loan[]>;
  type: LoanType;
  ratingFilter?: 'unrated';
  typeFilter: TypeFilter;
  onFilterChange: (filter: TypeFilter) => void;
  isHauteGaspesie: boolean;
}) => {
  const { t } = useTranslation(['loans', 'all-listings']);
  const width = useDeviceWidth();
  const userCurrency = useUserCurrency();
  const userInfo = useAtomValue(userInfoAtom);
  const data = (() => {
    const loans = query.data ?? [];
    if (ratingFilter !== 'unrated') {
      return loans;
    }

    return loans.filter((loan) => isLoanPendingCurrentUserRating(loan, userInfo?.id));
  })();
  const listData = data.toReversed();
  const totalSavings = Math.round(data.reduce((acc, loan) => acc + (loan.listing.estimatedPrice ?? 0), 0));

  const lendCount = data.filter((loan) => getLoanMode(loan) === TransactionMode.lend).length;
  const sellCount = data.filter((loan) => getLoanMode(loan) === TransactionMode.sell).length;
  const giveCount = data.filter((loan) => getLoanMode(loan) === TransactionMode.give).length;

  const filteredLoans = typeFilter === 'all' ? listData : listData.filter((loan) => getLoanMode(loan) === typeFilter);

  const filterHeader = isHauteGaspesie ? (
    <Tabs
      alignment="left"
      value={typeFilter}
      tabs={[
        { value: 'all', label: t('loans:past.filters.all'), extraLabel: String(data.length) },
        {
          value: TransactionMode.lend,
          label: t('all-listings:mode.lend'),
          extraLabel: String(lendCount),
        },
        {
          value: TransactionMode.sell,
          label: t('all-listings:mode.sell'),
          extraLabel: String(sellCount),
        },
        {
          value: TransactionMode.give,
          label: t('all-listings:mode.give'),
          extraLabel: String(giveCount),
        },
      ]}
      onPress={(value) => onFilterChange(value as TypeFilter)}
      variant="pill"
    />
  ) : null;

  return (
    <View style={{ flex: 1, width }}>
      <ListPage
        ListHeaderComponent={
          <Column gap="spacing.general.x4">
            {totalSavings > 0 ? (
              <View
                style={{
                  marginHorizontal: 4,
                  borderRadius: theme.borderRadii['radius.lg'],
                  backgroundColor: theme.colors['surface.success.subtle'],
                  paddingVertical: theme.spacing['spacing.general.x4'],
                  paddingHorizontal: theme.spacing['spacing.general.x5'],
                }}
              >
                <Row alignItems="center" gap="spacing.general.x3">
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 20,
                      backgroundColor: theme.colors['surface.default'],
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconLeaf strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.success']} />
                  </View>
                  <Column style={{ flex: 1 }} gap="spacing.general.x0,5">
                    <Text fontSize={22} fontWeight="700" color="text.success">
                      {formatCurrency(totalSavings, userCurrency, 0)}
                    </Text>
                    <Text fontSize={13} color="text.primary" style={{ opacity: 0.7 }}>
                      {t(`past.savings.header.${type}`)}
                    </Text>
                  </Column>
                </Row>
              </View>
            ) : null}
            {filterHeader ? (
              <View style={{ marginBottom: theme.spacing['spacing.general.x4'] }}>{filterHeader}</View>
            ) : null}
          </Column>
        }
        ListEmptyComponent={
          query.isLoading ? (
            <ActivityIndicator />
          ) : typeFilter !== 'all' ? (
            <Text color="text.tertiary" textAlign="center" variant="bodyBase">
              {t('loans:past.empty.filtered')}
            </Text>
          ) : (
            <EmptyGraphic
              image="EmptyFolder"
              invertTexts
              primary={t('past.empty.secondLine')}
              secondary={t('past.empty.firstLine')}
            />
          )
        }
        data={filteredLoans}
        onRefresh={() => void query.refetch()}
        refreshing={query.isRefetching}
        renderItem={({ item, index }) => (
          <TimelineLoanCard
            loan={item}
            isFirst={index === 0}
            isLast={index === filteredLoans.length - 1}
            isNewMonth={isTimelineLoanCardNewMonth(item, index > 0 ? filteredLoans[index - 1] : undefined)}
          />
        )}
      />
    </View>
  );
};
